/**
 * @file Utils to wrap fetch calls in test waiters
 *
 * Taken from ember/test-waiters package, since we're locked to 3.1.0 which doesn't export this utility.
 */

import { waitForPromise } from '@ember/test-waiters';

const props = [
  'body',
  'bodyUsed',
  'headers',
  'ok',
  'redirected',
  'status',
  'statusText',
  'type',
  'url',
] as const satisfies (keyof Response)[];
type ResponseProp = (typeof props)[number];
function isResponseProperty(maybeProp: string): maybeProp is ResponseProp {
  return props.includes(maybeProp);
}

const fns = [
  'arrayBuffer',
  'blob',
  // @ts-ignore
  'bytes',
  'clone',
  'formData',
  'json',
  'text',
] as const satisfies (keyof Response)[];
type ResponseFn = (typeof fns)[number];
function isResponseFn(maybeFn: string): maybeFn is ResponseFn {
  return fns.includes(maybeFn);
}

/**
 * Wraps the fetch promise in a test waiter, and also wraps the returned promises' async functions (like json()) in a
 * test waiter.
 */
export async function waitForFetch(fetchPromise: ReturnType<typeof fetch>) {
  const response = await waitForPromise(fetchPromise);

  return new Proxy(response, {
    get(target, prop, receiver) {
      /* Depending on the stack, Response is often already a Proxy. Reflect.get() will error for property values, when
       * using a Proxy as the _receiver_ arg, so just return the value the normal way to avoid that issue. */
      if (typeof prop === 'string' && isResponseProperty(prop)) {
        return target[prop];
      }
      const original = Reflect.get(target, prop, receiver);

      // Wrap Response functions in test waiter
      if (typeof prop === 'string' && isResponseFn(prop)) {
        // clone() is sync, no need to wrap in test-waiter
        if (prop === 'clone') {
          return (...args: unknown[]) => {
            return original.call(target, ...args);
          };
        }
        return (...args: unknown[]) => {
          return waitForPromise(original.call(target, ...args));
        };
      }
      // return the Reflect.get() result for anything else
      return original;
    },
  });
}
